import { Capacitor } from '@capacitor/core';
import { trackEvent } from '../utils/analytics';

const isNative = Capacitor.isNativePlatform();

/**
 * Check if push notifications are supported on this platform.
 */
export function isPushSupported() {
    if (isNative) {
        return true;
    }
    return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
}

/** Why push can't be enabled right now: 'denied' | 'needs_install' | null. */
export function getPushBlocker() {
    if (isNative) return null;
    if (typeof Notification !== 'undefined' && Notification.permission === 'denied') return 'denied';
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const standalone = window.navigator.standalone === true || window.matchMedia?.('(display-mode: standalone)').matches;
    if (isIOS && !standalone && !('PushManager' in window)) return 'needs_install';
    return null;
}

/**
 * Initialize push notifications.
 * Returns subscription info if granted, otherwise { failure } with the reason:
 * 'denied' | 'dismissed' | 'no_key' | 'register_error' | 'timeout' | 'error'.
 *
 * For web: { platform: 'web', endpoint, p256dh_key, auth_key }
 * For native: { platform: 'ios'|'android', device_token }
 */
export async function initPushNotifications() {
    if (isNative) {
        return initNativePush();
    }
    return initWebPush();
}

/**
 * Web Push via standard Web Push API + VAPID.
 */
async function initWebPush() {
    try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        // Wait for the service worker to be ready
        await navigator.serviceWorker.ready;

        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
            return { failure: permission === 'denied' ? 'denied' : 'dismissed' };
        }

        // Convert VAPID public key from base64url to Uint8Array
        const vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
        if (!vapidPublicKey) {
            console.error('REACT_APP_VAPID_PUBLIC_KEY not configured');
            return { failure: 'no_key' };
        }
        const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);

        const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey,
        });

        const subJson = subscription.toJSON();
        return {
            platform: 'web',
            endpoint: subJson.endpoint,
            p256dh_key: subJson.keys.p256dh,
            auth_key: subJson.keys.auth,
        };
    } catch (error) {
        console.error('Web push init error:', error);
        return { failure: 'error' };
    }
}

/**
 * Native Push via Capacitor PushNotifications plugin.
 */
async function initNativePush() {
    try {
        const { PushNotifications } = await import('@capacitor/push-notifications');

        const permResult = await PushNotifications.requestPermissions();
        if (permResult.receive !== 'granted') {
            return { failure: permResult.receive === 'denied' ? 'denied' : 'dismissed' };
        }

        await PushNotifications.register();

        return new Promise((resolve) => {
            let resolved = false;

            const timer = setTimeout(() => {
                if (!resolved) {
                    resolved = true;
                    console.error('Push registration timed out after 15s');
                    resolve({ failure: 'timeout' });
                }
            }, 15000);

            PushNotifications.addListener('registration', (token) => {
                if (!resolved) {
                    resolved = true;
                    clearTimeout(timer);
                    resolve({
                        platform: Capacitor.getPlatform(), // 'ios' or 'android'
                        device_token: token.value,
                    });
                }
            });

            PushNotifications.addListener('registrationError', (error) => {
                if (!resolved) {
                    resolved = true;
                    clearTimeout(timer);
                    console.error('Push registration error:', error);
                    resolve({ failure: 'register_error' });
                }
            });

            // Handle notification tapped (opens app from background)
            PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
                trackEvent('Push Opened', { type: action?.notification?.data?.type || 'unknown' });
            });
        });
    } catch (error) {
        console.error('Native push init error:', error);
        return { failure: 'error' };
    }
}

/**
 * Convert a base64url-encoded string to a Uint8Array.
 * Required for applicationServerKey in PushManager.subscribe().
 */
function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}
